import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { X } from 'lucide-react';
import { userService } from '../../services/userService';
import { UserProfile as UserProfileType } from '../../types/user';
import { InlineEditField } from './InlineEditField';
import { InlineEditSelect } from './InlineEditSelect';
import { InlineEditToggle } from './InlineEditToggle';
import { DEFAULT_URLS } from '../../config/defaults';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, onClose }) => {
  const { user, isDemoMode, refreshUser } = useAuth();

  if (!isOpen) return null;

  // Field update handlers for inline editing
  const handleFieldUpdate = async <T extends keyof UserProfileType>(
    field: T,
    value: UserProfileType[T]
  ) => {
    if (!user) throw new Error('User not authenticated');
    
    await userService.updateField(field, value);
    await refreshUser();
  };

  const handleDisplayNameUpdate = (value: string) => handleFieldUpdate('displayName', value);
  const handleEmailUpdate = (value: string) => handleFieldUpdate('email', value);
  const handleLanguageUpdate = (value: string) => handleFieldUpdate('language', value);
  const handleTimezoneUpdate = (value: string) => handleFieldUpdate('timezone', value);
  
  const handleDataProcessingConsentUpdate = async (consented: boolean) => {
    if (!user) throw new Error('User not authenticated');
    
    const updates: Partial<UserProfileType> = {
      metadata: {
        ...user.metadata,
        dataProcessingConsent: consented
      }
    };
    
    await userService.updateProfile(updates);
    await refreshUser();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-card shadow-card max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-rm-neutral-600/20">
          <h2 className="text-2xl font-bold text-rm-primary-700">Profile Settings</h2>
          <button
            onClick={onClose}
            className="text-rm-neutral-600 hover:text-rm-ink-900 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Demo Mode Warning */}
          {isDemoMode && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
              <p className="text-sm text-amber-800">
                You are in <strong>Demo Mode</strong>. Profile changes are temporary.
              </p>
            </div>
          )}

          {user && (
            <div className="space-y-6">
              <InlineEditField
                value={user.displayName}
                onSave={handleDisplayNameUpdate}
                label="Display Name"
                placeholder="Enter display name"
                type="text"
                required
                maxLength={100}
                validation={(value) => {
                  if (value.trim().length === 0) return 'Display name is required';
                  if (value.trim().length > 100) return 'Display name cannot exceed 100 characters';
                  return null;
                }}
              />
              
              <InlineEditField
                value={user.email}
                onSave={handleEmailUpdate}
                label="Email"
                placeholder="Enter email address"
                type="email"
                required
                maxLength={255}
                validation={(value) => {
                  if (!value.includes('@')) return 'Invalid email address';
                  if (value.length > 255) return 'Email cannot exceed 255 characters';
                  return null;
                }}
              />
              
              <InlineEditSelect
                value={user.language}
                onSave={handleLanguageUpdate}
                label="Language"
                options={[
                  { value: 'en', label: 'English' },
                  { value: 'nl', label: 'Dutch' }
                ]}
                required
              />

              <InlineEditSelect
                value={user.timezone || 'Europe/Amsterdam'}
                onSave={handleTimezoneUpdate}
                label="Timezone"
                options={[
                  { value: 'Europe/Amsterdam', label: 'Europe/Amsterdam (CET)' },
                  { value: 'UTC', label: 'UTC' },
                  { value: 'Europe/London', label: 'Europe/London (GMT)' },
                  { value: 'Europe/Berlin', label: 'Europe/Berlin (CET)' },
                  { value: 'Europe/Paris', label: 'Europe/Paris (CET)' },
                  { value: 'America/New_York', label: 'America/New_York (EST)' },
                  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST)' },
                  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
                  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEDT)' }
                ]}
                required
              />
              
              {/* Roles Display */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-rm-ink-900">
                  Roles
                </label>
                <div className="flex flex-wrap gap-2">
                  {user.roles.map((role) => (
                    <span
                      key={role}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        role === 'admin' 
                          ? 'bg-red-100 text-red-800'
                          : role === 'reportsReader'
                          ? 'bg-rm-accent-300 text-rm-ink-900'
                          : role === 'moderator'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-rm-bg-tint text-rm-ink-900'
                      }`}
                    >
                      {role === 'reportsReader' ? 'Reports Reader' : 
                       role.charAt(0).toUpperCase() + role.slice(1)}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-rm-neutral-600">
                  Your account roles determine which features and sections you can access.
                </p>
              </div>
              
              {/* Data Processing Consent - only show if consent configuration exists */}
              {user.consent && (
                <InlineEditToggle
                  value={user.metadata.dataProcessingConsent || false}
                  onSave={handleDataProcessingConsentUpdate}
                  label="Data Processing Consent"
                  trueLabel="Consented"
                  falseLabel="Withdraw Consent"
                  displayAs="badge"
                />
              )}
              
              {/* Privacy Notice Link - always show */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-rm-ink-900">
                  Privacy Notice
                </label>
                <a
                  href={user.consent?.privacyUrl || DEFAULT_URLS.PRIVACY_POLICY}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm text-rm-accent-600 hover:text-rm-accent-700 underline font-medium transition-colors"
                >
                  View Privacy Notice
                  <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
                <p className="text-xs text-rm-neutral-600">
                  Read our privacy notice to understand how we process your data.
                </p>
              </div>
              
              {/* User ID */}
              <div className="text-xs text-rm-neutral-600">
                <span className="font-medium">User ID:</span>{' '}
                <code className="font-mono">{user.id.includes('@') ? user.id.split('@')[0] : user.id.substring(0, 8)}</code>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-rm-neutral-600/20">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-pill bg-rm-accent-500 text-white hover:opacity-90 transition-opacity"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
